import { PlusIcon } from '@radix-ui/react-icons'
import { Flex } from '@radix-ui/themes'
import { DataTestIDs } from 'shared/constants'
import { FlashIcon, WalletIcon } from 'shared/icons'
import { NeonButton, NeonPanel } from 'shared/ui/neon'
import { shortenAddress } from 'shared/utils/shorten-address'
import type { WalletBalance } from '../types'
import { CopyAddressButton } from './copy-address-button'
import { WalletBalances } from './wallet-balances'
import styles from '../neon-wallets.module.scss'

interface Props {
  address: string
  isPrimary: boolean
  isSelected: boolean
  isConnected: boolean
  /** The embedded wallet Privy created at sign-up. */
  isEmbedded?: boolean
  balance?: WalletBalance
  onSelect: (address: string) => void
  onDeposit: (address: string) => void
}

/** One linked wallet: what it holds, whether it plays, and how to top it up. */
export const WalletListItem: React.FC<Props> = ({
  address,
  isPrimary,
  isSelected,
  isConnected,
  isEmbedded = false,
  balance,
  onSelect,
  onDeposit
}) => (
  <NeonPanel
    glow={isSelected ? 'green' : 'none'}
    padding='1'
    gap='3'
    data-selected={isSelected}
    data-testid={DataTestIDs.walletsSettingsRow}
  >
    <Flex
      align='center'
      gap='3'
    >
      <span
        className={styles.walletAvatar}
        data-selected={isSelected}
      >
        <WalletIcon />
      </span>

      <Flex
        direction='column'
        minWidth='0'
        flexGrow='1'
      >
        <Flex
          align='center'
          gap='2'
          wrap='wrap'
        >
          <span className={styles.walletName}>
            {isEmbedded ? 'XYRO wallet' : 'External wallet'}
          </span>
          {isPrimary ?
            <span
              className={styles.chip}
              data-tone='gold'
            >
              Primary
            </span>
          : null}
          {isConnected ?
            <span
              className={styles.chip}
              data-tone='green'
            >
              Connected
            </span>
          : null}
        </Flex>
        <Flex
          align='center'
          gap='1'
        >
          <span
            className={styles.walletAddress}
            title={address}
          >
            {shortenAddress(address)}
          </span>
          <CopyAddressButton
            address={address}
            testId={DataTestIDs.buttonPrivyWalletCopy}
          />
        </Flex>
      </Flex>

      {isSelected ?
        <span className={styles.playingChip}>
          <FlashIcon />
          Playing
        </span>
      : null}
    </Flex>

    <WalletBalances balance={balance} />

    <Flex
      gap='2'
      className={styles.cardActions}
    >
      <NeonButton
        type='button'
        iconLeft={<PlusIcon />}
        onClick={() => onDeposit(address)}
        data-testid={DataTestIDs.buttonWalletDeposit}
      >
        Top up
      </NeonButton>
      {isSelected ? null : (
        <button
          type='button'
          className={styles.secondaryButton}
          onClick={() => onSelect(address)}
          data-testid={DataTestIDs.buttonSelectPlayWallet}
        >
          Play with this wallet
        </button>
      )}
    </Flex>
  </NeonPanel>
)
